import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import "./viewProjects.css";
import SearchBar from "../Main/searchBar";
import { Link, useParams } from "react-router-dom";
import { ReactComponent as BackIcon } from "../../Assets/Categories/BackIcon.svg";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import "./viewProperties.css";
import AmenitiesList from "./amenitiesList";
import { viewProperty, getMarginHistory, updatePropertyPrice } from "../../Redux/Actions/propertyActions";
import RewardPointsPopup from "./rewardPopup";
import AdminMarginPopup from "./addAdminMarginPopup";
import MarginRatioPopup from "./addMarginRatioPopup";
import { DateToLocale, ToFirstCapital } from "../../Utils/helpers";

const ViewProperties = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { id } = useParams();
  const popupRef = useRef(null);

  const [amenityPopup, setAmenityPopup] = useState(false);
  const [rewardPopup, setRewardPopup] = useState(false);
  const [adminMarginPopup, setAdminMarginPopup] = useState(false);
  const [marginRatioPopup, setMarginRatioPopup] = useState(false);
  const [visibleImages, setVisibleImages] = useState(6);
  const [previewImage, setPreviewImage] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("");
  const [zoom, setZoom] = useState(1);
  const [activeTab, setActiveTab] = useState("overview");

  const [pricePopup, setPricePopup] = useState(false);
  const [selectedPriceType, setSelectedPriceType] = useState("");
  const [priceValue, setPriceValue] = useState("");

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
  const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');

  const isSuperAdmin = admin?.data?.role === "super_admin";

  const closePreview = () => {
    setPreviewImage(null);
    setZoom(1);
  };

  const { enumsData } = useSelector((state) => state.enumsData);
  const { properties, property } = useSelector((state) => state.properties);
  const currency = property?.data?.currency || "";

  const dispatch = useDispatch();

  const changeStatusHandler = async (event) => {
    try {
      const selectedOption = event.target.value;
      if (!selectedOption) return;
      await axios
        .post(
          `${BASE_URL}/property/change-status`,
          { property_id: id, status: selectedOption },
          { headers: { "Content-Type": "application/json" } }
        )
        .then(async () => {
          toast.success(`Property Published Successfully!`);
          setCurrentStatus(selectedOption);
          dispatch(viewProperty(id));
        });
    } catch (error) {
      toast.error(error?.response?.data?.message);
      console.error("Error at changing status", error.response);
    }
  };

  const handleUpdatePrice = async () => {
    if (!selectedPriceType || !priceValue) return toast.error("Please fill all fields");
    const result = await dispatch(updatePropertyPrice({
      property_id: id,
      price_type: selectedPriceType,
      price: Number(priceValue)
    }));
    if (result?.statusCode === 200) {
      dispatch(viewProperty(id));
      setPricePopup(false);
      setPriceValue("");
      setSelectedPriceType("");
    }
  };

  const togglePopup = useCallback(() => setAmenityPopup(prev => !prev), []);
  const toggleRewardPopup = useCallback(() => {
    if (rewardPopup) dispatch(viewProperty(id));
    setRewardPopup(prev => !prev);
  }, [rewardPopup, dispatch, id]);
  const toggleAdminMarginPopup = useCallback(() => setAdminMarginPopup(prev => !prev), []);
  const toggleMarginRatioPopup = useCallback(() => setMarginRatioPopup(prev => !prev), []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setAmenityPopup(false);
      }
    };
    if (amenityPopup) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [amenityPopup]);

  useEffect(() => {
    dispatch(viewProperty(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (id) dispatch(getMarginHistory(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (property?.data?.status) setCurrentStatus(property.data.status);
  }, [property?.data?.status]);

  const foundProperty = useMemo(() => {
    return properties?.data?.find(p => p._id === id);
  }, [properties, id]);

  const totalPrice = useMemo(() => {
    const prices = [
      property?.data?.furnished_price,
      property?.data?.semi_furnished_price,
      property?.data?.unfurnished_price
    ].find(price => price > 1);
    return prices ? prices.toLocaleString("en-IN") : "N/A";
  }, [property?.data?.furnished_price, property?.data?.semi_furnished_price, property?.data?.unfurnished_price]);

  const existingAdminMargin = useMemo(() => {
    const obj = property?.data?.admin_margin;
    if (obj && typeof obj === "object" && obj.type && obj.margin != null) {
      return {
        admin_margin_type: String(obj.type),
        admin_margin_value: String(obj.margin),
      };
    }
    return null;
  }, [property?.data?.admin_margin]);

  const existingMarginRatio = useMemo(() => {
    const obj = property?.data?.margin_ratio;
    if (obj && typeof obj === "object") {
      const agent = obj.agent_margin != null ? obj.agent_margin : "";
      const admin = obj.admin_margin != null ? obj.admin_margin : "";
      if (agent !== "" || admin !== "") {
        return { agent_margin: agent, admin_margin: admin };
      }
    }
    return null;
  }, [property?.data?.margin_ratio]);

  const marginSectionStyles = {
    wrapper: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" },
    card: { background: "#fff", borderRadius: "16px", padding: "20px", border: "1px solid #e2e8f0", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" },
    cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
    cardTitleRow: { display: "flex", alignItems: "center", gap: "10px" },
    cardIcon: { fontSize: "20px" },
    cardTitle: { fontSize: "15px", fontWeight: 700, color: "#0f172a" },
    editBtn: { padding: "6px 14px", borderRadius: "8px", border: "1px solid #2c4791", background: "rgba(44,71,145,0.06)", color: "#2c4791", fontSize: "13px", fontWeight: 600, cursor: "pointer" },
    dataGrid: { display: "flex", flexDirection: "column", gap: "12px" },
    dataItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #f1f5f9" },
    dataLabel: { color: "#64748b", fontSize: "13px", fontWeight: 600 },
    typeBadge: { background: "rgba(44,71,145,0.08)", color: "#2c4791", padding: "4px 12px", borderRadius: "20px", fontSize: "13px", fontWeight: 600, border: "1px solid rgba(44,71,145,0.2)" },
    dataValue: { color: "#0f172a", fontSize: "16px", fontWeight: 700 },
    ratioGrid: { display: "flex", alignItems: "center", justifyContent: "space-around", background: "#f8fafc", borderRadius: "12px", padding: "16px", border: "1px solid #f1f5f9" },
    ratioItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" },
    ratioEmoji: { fontSize: "20px" },
    ratioLabel: { color: "#64748b", fontSize: "12px", fontWeight: 600 },
    ratioValue: { color: "#0f172a", fontSize: "20px", fontWeight: 700 },
    ratioDivider: { width: "1px", height: "40px", background: "#e2e8f0" },
    emptyState: { display: "flex", alignItems: "center", gap: "10px", padding: "16px", background: "#f8fafc", borderRadius: "10px", border: "1px dashed #cbd5e1" },
    emptyIcon: { color: "#94a3b8", fontSize: "18px", fontWeight: 700 },
    emptyText: { color: "#94a3b8", fontSize: "13px", fontStyle: "italic" },
  };

  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridAutoRows: "minmax(60px, auto)", background: "#F5F7FA" }}
      className="padding-20 gap-10 grid-container"
    >
      <div style={{ gridColumn: "4/8" }}>
        <SearchBar style={{ width: "max-content" }} />
      </div>

      <div className="back-icon margin-0 gap-10 align-center flex-container" style={{ gridColumn: "span 7" }}>
        <Link to="/manage_properties"><BackIcon style={{ cursor: "pointer" }} /></Link>
        <div>
          <h3>{property?.data?.project?.project_name}</h3>
          <h4 style={{ position: "relative", top: "-10px" }}>Property/ List/ View</h4>
        </div>
      </div>

      <div className="modern-property-view">
        {/* Hero Banner */}
        <div className="hero-banner">
          <div className="hero-image-container">
            <img src={property?.data?.cover_photos?.[0]?.url} alt="Property" loading="eager" decoding="async" className="hero-main-image" />
            <div className="hero-overlay">
              <div className="hero-badges">
                <span className="status-pill construction">{property?.data?.construction_status || "N/A"}</span>
                <span className="status-pill featured">Featured</span>
              </div>
            </div>
          </div>

          <div className="hero-content-card">
            <div className="project-header">
              <div>
                <h1 className="project-name">{property?.data?.project?.project_name || "N/A"}</h1>
                <p className="project-meta">
                  <span>📍</span> {property?.data?.project?.brand_name || "N/A"} • ID: {property?.data?.project?.project_id || "N/A"}
                </p>
              </div>
              <div>
                <span className="price-label">Starting Price</span>
                <div className="price-tag-large">
                  <span className="price-value">{currency} {totalPrice}</span>
                </div>
              </div>
            </div>

            <div className="quick-stats">
              <div className="quick-stat-item">
                <span className="stat-icon">🏠</span>
                <div><span className="stat-number">{property?.data?.no_of_bedroom || "0"}</span><span className="stat-label">Bedrooms</span></div>
              </div>
              <div className="quick-stat-item">
                <span className="stat-icon">📐</span>
                <div><span className="stat-number">{property?.data?.unit_area || "-"}</span><span className="stat-label">Sq.ft</span></div>
              </div>
              <div className="quick-stat-item">
                <span className="stat-icon">💰</span>
                <div><span className="stat-number">{currency} {Number(property?.data?.price_per_sq_ft)?.toLocaleString() || "N/A"}</span></div>
              </div>
              <div className="quick-stat-item">
                <span className="stat-icon">📅</span>
                <div><span className="stat-number">{property?.data?.project?.launch_date?.slice(0, 7) || "N/A"}</span><span className="stat-label">Launch Date</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-navigation">
          {["overview", "details", "gallery"].map((tab) => (
            <button key={tab} className={`tab-btn ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
              {tab === "overview" ? "Overview" : tab === "details" ? "Specifications" : "Gallery"}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            <div className="info-grid-modern">
              <div className="info-section-card">
                <h3 className="section-title">Property Information</h3>
                <div className="info-rows">
                  {[
                    ["Type", property?.data?.unit?.name],
                    ["Category", property?.data?.project_type?.name],
                    ["RERA ID", property?.data?.project?.rera_id],
                    ["Tower Name", property?.data?.tower_name || "No Tower"],
                    ["Upload Date", DateToLocale(property?.data?.created_at)],
                  ].map(([key, val]) => (
                    <div className="info-row" key={key}>
                      <span className="info-key">{key}</span>
                      <span className="info-value" style={{ background: "transparent" }}>{val || "N/A"}</span>
                    </div>
                  ))}
                  <div className="info-row">
                    <span className="info-key">Status</span>
                    <span className="status-badge active">{ToFirstCapital(property?.data?.status) || "N/A"}</span>
                  </div>
                </div>
              </div>

              <div className="info-section-card">
                <h3 className="section-title">Configuration</h3>
                <div className="room-grid">
                  {[
                    ["🛏️", property?.data?.no_of_bedroom, "Bedrooms"],
                    ["🚿", property?.data?.no_of_bathroom, "Bathrooms"],
                    ["🍳", property?.data?.no_of_kitchen, "Kitchen"],
                    ["🌅", property?.data?.no_of_balcony, "Balcony"],
                    ["☕", property?.data?.no_of_pantry, "Pantry"],
                  ].map(([emoji, count, label]) => (
                    <div className="room-item" key={label}>
                      <span className="room-emoji">{emoji}</span>
                      <span className="room-count">{count || "0"}</span>
                      <span className="room-type">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="features-section">
              <div className="feature-card accent-blue">
                <h4 className="feature-title">Property Facing</h4>
                <div className="feature-tags">
                  {Array.isArray(property?.data?.property_facing) && property.data.property_facing.length > 0
                    ? property.data.property_facing.map((f, i) => <span key={i} className="feature-tag blue">{ToFirstCapital(f)}</span>)
                    : <span className="no-data-text">No data</span>}
                </div>
              </div>
              <div className="feature-card accent-green">
                <h4 className="feature-title">Property Views</h4>
                <div className="feature-tags">
                  {Array.isArray(property?.data?.property_view) && property.data.property_view.length > 0
                    ? property.data.property_view.map((v, i) => <span key={i} className="feature-tag green">{ToFirstCapital(v)}</span>)
                    : <span className="no-data-text">No data</span>}
                </div>
              </div>
              <div className="feature-card accent-purple">
                <h4 className="feature-title">Additional Rooms</h4>
                <div className="feature-tags">
                  {Array.isArray(property?.data?.additional_rooms) && property.data.additional_rooms.length > 0
                    ? property.data.additional_rooms.map((r, i) => <span key={i} className="feature-tag purple">{r}</span>)
                    : <span className="no-data-text">No additional rooms</span>}
                </div>
              </div>
            </div>

            {/* Action Cards */}
            <div className="action-cards-modern">
              <div className="action-card-modern" onClick={togglePopup}>
                <span className="action-icon-modern">✨</span>
                <div className="action-content"><h4>Amenities</h4><p>Click to view all amenities</p></div>
                <span className="action-arrow">→</span>
              </div>
              <div className="action-card-modern" onClick={toggleRewardPopup}>
                <span className="action-icon-modern">🎯</span>
                <div className="action-content"><h4>Reward Points</h4><p>Configure rewards</p></div>
                <span className="action-arrow">→</span>
              </div>
              <div className="action-card-modern discount">
                <span className="action-icon-modern">🎁</span>
                <div className="action-content">
                  <h4 style={{ color: "white" }}>Special Offer</h4>
                  <p>{property?.data?.discount_type || "No Discount"}</p>
                </div>
              </div>
            </div>

            {/* ✅ Property Pricing — only super_admin */}
            {isSuperAdmin && (
              <div style={{
                background: "#fff", borderRadius: "16px", padding: "20px",
                border: "1px solid #e2e8f0", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: "24px"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "20px" }}>💵</span>
                    <span style={{ fontSize: "15px", fontWeight: 700, color: "#0f172a" }}>Property Pricing</span>
                  </div>
                  <button
                    onClick={() => {
                      // ✅ Find first available price type
                      const availablePrices = [
                        { label: "Furnished Price", key: "furnished_price" },
                        { label: "Semi Furnished Price", key: "semi_furnished_price" },
                        { label: "Unfurnished Price", key: "unfurnished_price" },
                      ].filter(({ key }) => property?.data?.[key] > 1);

                      if (availablePrices.length > 0) {
                        const firstKey = availablePrices[0].key;
                        setSelectedPriceType(firstKey);
                        setPriceValue(String(property.data[firstKey]));
                      }

                      setPricePopup(true);
                    }}
                    style={{ padding: "6px 14px", borderRadius: "8px", border: "1px solid #2c4791", background: "rgba(44,71,145,0.06)", color: "#2c4791", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
                  >
                    ✏️ Update Price
                  </button>
                </div>

                {/* ✅ Only show prices that exist (> 1) */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
                  {[
                    { label: "Furnished Price", key: "furnished_price" },
                    { label: "Semi Furnished Price", key: "semi_furnished_price" },
                    { label: "Unfurnished Price", key: "unfurnished_price" },
                  ]
                    .filter(({ key }) => property?.data?.[key] > 1)  // ✅ only show if has value
                    .map(({ label, key }) => (
                      <div key={key} style={{ padding: "10px 14px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #f1f5f9" }}>
                        <div style={{ color: "#64748b", fontSize: "13px", fontWeight: 600, marginBottom: "4px" }}>{label}</div>
                        <div style={{ color: "#0f172a", fontSize: "16px", fontWeight: 700 }}>
                          {currency} {Number(property.data[key]).toLocaleString("en-IN")}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {canAdd && canEdit &&
              <div style={marginSectionStyles.wrapper}>
                {/* Admin Margin */}
                <div style={marginSectionStyles.card}>
                  <div style={marginSectionStyles.cardHeader}>
                    <div style={marginSectionStyles.cardTitleRow}>
                      <span style={marginSectionStyles.cardIcon}>💰</span>
                      <span style={marginSectionStyles.cardTitle}>Admin Margin</span>
                    </div>
                    {(canAdd || canEdit) && (
                      <button style={marginSectionStyles.editBtn} onClick={toggleAdminMarginPopup}>
                        {existingAdminMargin ? "✏️ Edit" : "+ Add"}
                      </button>
                    )}
                  </div>
                  {existingAdminMargin ? (
                    <div style={marginSectionStyles.dataGrid}>
                      <div style={marginSectionStyles.dataItem}>
                        <span style={marginSectionStyles.dataLabel}>Type</span>
                        <span style={marginSectionStyles.typeBadge}>
                          {existingAdminMargin.admin_margin_type === "percentage" && "% Percentage"}
                          {existingAdminMargin.admin_margin_type === "flat" && "₹ Flat"}
                          {existingAdminMargin.admin_margin_type === "per_sq_ft" && "□ Per Sq.ft"}
                          {!["percentage", "flat", "per_sq_ft"].includes(existingAdminMargin.admin_margin_type) && existingAdminMargin.admin_margin_type}
                        </span>
                      </div>
                      <div style={marginSectionStyles.dataItem}>
                        <span style={marginSectionStyles.dataLabel}>Value</span>
                        <span style={marginSectionStyles.dataValue}>
                          {existingAdminMargin.admin_margin_type === "percentage"
                            ? `${existingAdminMargin.admin_margin_value}%`
                            : `₹ ${Number(existingAdminMargin.admin_margin_value).toLocaleString("en-IN")}`}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div style={marginSectionStyles.emptyState}>
                      <span style={marginSectionStyles.emptyIcon}>—</span>
                      <span style={marginSectionStyles.emptyText}>No admin margin configured</span>
                    </div>
                  )}
                </div>

                {/* Margin Ratio */}
                <div style={marginSectionStyles.card}>
                  <div style={marginSectionStyles.cardHeader}>
                    <div style={marginSectionStyles.cardTitleRow}>
                      <span style={marginSectionStyles.cardIcon}>📊</span>
                      <span style={marginSectionStyles.cardTitle}>Margin Ratio</span>
                    </div>
                    {(canAdd || canEdit) && (
                      <button style={marginSectionStyles.editBtn} onClick={toggleMarginRatioPopup}>
                        {existingMarginRatio ? "✏️ Edit" : "+ Add"}
                      </button>
                    )}
                  </div>
                  {existingMarginRatio ? (
                    <div style={marginSectionStyles.ratioGrid}>
                      <div style={marginSectionStyles.ratioItem}>
                        <span style={marginSectionStyles.ratioEmoji}>🧑‍💼</span>
                        <span style={marginSectionStyles.ratioLabel}>Agent</span>
                        <span style={marginSectionStyles.ratioValue}>{Number(existingMarginRatio.agent_margin) || 0}%</span>
                      </div>
                      <div style={marginSectionStyles.ratioDivider} />
                      <div style={marginSectionStyles.ratioItem}>
                        <span style={marginSectionStyles.ratioEmoji}>🏛️</span>
                        <span style={marginSectionStyles.ratioLabel}>Admin</span>
                        <span style={marginSectionStyles.ratioValue}>{Number(existingMarginRatio.admin_margin) || 0}%</span>
                      </div>
                      <div style={marginSectionStyles.ratioDivider} />
                      <div style={marginSectionStyles.ratioItem}>
                        <span style={marginSectionStyles.ratioEmoji}>∑</span>
                        <span style={marginSectionStyles.ratioLabel}>Total</span>
                        <span style={{ ...marginSectionStyles.ratioValue, color: "#10b981" }}>
                          {(Number(existingMarginRatio.agent_margin) || 0) + (Number(existingMarginRatio.admin_margin) || 0)}%
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div style={marginSectionStyles.emptyState}>
                      <span style={marginSectionStyles.emptyIcon}>—</span>
                      <span style={marginSectionStyles.emptyText}>No margin ratio configured</span>
                    </div>
                  )}
                </div>
              </div>
            }

            {canAdd && (
              <div className="status-selector-container">
                <label className="selector-label">Change Property Status</label>
                <select value={currentStatus} onChange={changeStatusHandler} style={{ width: "100%", borderRadius: "10px" }}>
                  <option value="" disabled>Select Status</option>
                  {enumsData?.data?.PropertyStatus
                    ? Object.entries(enumsData.data.PropertyStatus).map(([key, value]) => (
                      <option key={key} value={value}>{ToFirstCapital(value)}</option>
                    ))
                    : <option disabled>No status options available</option>}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="tab-content">
            <div className="specifications-grid">
              <div className="spec-card">
                <h4>Area Details</h4>
                <div className="spec-items">
                  <div className="spec-item">
                    <span>Carpet Area</span>
                    <strong>{property?.carpet_area ? `${property.carpet_area} sq.ft` : "N/A"}</strong>
                  </div>
                  <div className="spec-item">
                    <span>Unit Area</span>
                    <strong>{property?.unit_area ? `${property.unit_area} ${property?.unit_area_measure_in || ""}` : "N/A"}</strong>
                  </div>                </div>
              </div>
              <div className="spec-card">
                <h4>Parking</h4>
                <div className="spec-items">
                  <div className="spec-item"><span>🚗 Open Parking</span><strong>{property?.data?.open_parking || "0"} spaces</strong></div>
                  <div className="spec-item"><span>🏠 Covered Parking</span><strong>{property?.data?.cover_parking || "0"} spaces</strong></div>
                </div>
              </div>
              <div className="spec-card full-width">
                <h4>Pricing Breakdown</h4>
                <div className="pricing-table">
                  <div className="pricing-row"><span>Base Price per sq.ft</span><strong>₹{property?.data?.price_per_sq_ft?.toLocaleString() || "N/A"}</strong></div>
                  <div className="pricing-row">
                    <span>Total Area</span>
                    <strong>{property?.data?.unit_area ? `${property.data.unit_area} sq.ft` : "N/A"}</strong>
                  </div>                  <div className="pricing-row total"><span>Starting Price</span><strong style={{ color: "white" }}>{currency} {totalPrice}</strong></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="tab-content">
            <div className="gallery-section-modern">
              <h3 className="gallery-title-modern">📷 Cover Photos</h3>
              <div className="gallery-modern">
                {Array.isArray(property?.data?.cover_photos) && property.data.cover_photos.length > 0
                  ? property.data.cover_photos.map((photo, index) => (
                    <div key={index} className="gallery-item-modern" onClick={() => setPreviewImage(photo.url)}>
                      <img src={photo?.url} loading="lazy" alt={`Cover ${index + 1}`} />
                      <div className="gallery-overlay"><span>View Full Size</span></div>
                    </div>
                  ))
                  : <p className="no-data-text">No cover photos available</p>}
              </div>
            </div>
            <div className="gallery-section-modern">
              <h3 className="gallery-title-modern">📸 Property Gallery</h3>
              <div className="gallery-modern">
                {Array.isArray(property?.data?.images) && property.data.images.length > 0
                  ? property.data.images.slice(0, visibleImages).map((image, idx) => (
                    <div key={idx} className="gallery-item-modern" onClick={() => setPreviewImage(image.url)}>
                      <img src={image.url} loading="lazy" alt={`Property ${idx + 1}`} />
                      <div className="gallery-overlay"><span>View Full Size</span></div>
                    </div>
                  ))
                  : <p className="no-data-text">No images available</p>}
              </div>
              {property?.data?.images && visibleImages < property.data.images.length && (
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                  <button onClick={() => setVisibleImages(v => v + 6)} style={{ padding: "10px 20px", borderRadius: "8px", border: "none", cursor: "pointer", background: "#2c4791", color: "white", fontWeight: "600" }}>
                    Load More
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Amenity Popup */}
      {amenityPopup && (
        <div onClick={togglePopup}>
          <div ref={popupRef} onClick={(e) => e.stopPropagation()}>
            <AmenitiesList propertyData={foundProperty || []} onClose={togglePopup} />
          </div>
        </div>
      )}

      <RewardPointsPopup isOpen={rewardPopup} onClose={() => { dispatch(viewProperty(id)); setRewardPopup(false); }} propertyId={id} />

      <AdminMarginPopup isOpen={adminMarginPopup} onClose={() => { dispatch(viewProperty(id)); setAdminMarginPopup(false); }} propertyId={id} existingData={existingAdminMargin} />

      <MarginRatioPopup isOpen={marginRatioPopup} onClose={() => { dispatch(viewProperty(id)); setMarginRatioPopup(false); }} propertyId={id} existingData={existingMarginRatio} />

      {pricePopup && isSuperAdmin && (
        <div
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)" }}
          onClick={() => setPricePopup(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: "20px", padding: "32px", width: "90%", maxWidth: "440px", border: "1px solid #e2e8f0", boxShadow: "0 24px 48px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 24px 0", color: "#0f172a", fontSize: "18px", fontWeight: 700 }}>
              💵 Update Property Price
            </h3>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", color: "#64748b", fontSize: "13px", fontWeight: 600, marginBottom: "8px" }}>
                Price Type
              </label>
              <select
                value={selectedPriceType}
                onChange={(e) => {
                  setSelectedPriceType(e.target.value);
                  // ✅ Pre-fill current value when price type is selected
                  setPriceValue(property?.data?.[e.target.value] > 1 ? property.data[e.target.value] : "");
                }}
                style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: "14px", outline: "none", cursor: "pointer" }}
              >
                <option value="" disabled>Select price type</option>
                {/* ✅ Only show options that have a value */}
                {[
                  { label: "Furnished Price", key: "furnished_price" },
                  { label: "Semi Furnished Price", key: "semi_furnished_price" },
                  { label: "Unfurnished Price", key: "unfurnished_price" },
                ]
                  .filter(({ key }) => property?.data?.[key] > 1)
                  .map(({ label, key }) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
              </select>
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={{ display: "block", color: "#64748b", fontSize: "13px", fontWeight: 600, marginBottom: "8px" }}>
                Price ({currency})
              </label>
              <input
                type="number"
                value={priceValue}
                onChange={(e) => setPriceValue(e.target.value)}
                placeholder="Enter price"
                style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: "14px", outline: "none", boxSizing: "border-box" }}
              />
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => { setPricePopup(false); setPriceValue(""); setSelectedPriceType(""); }}
                style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "1px solid #e2e8f0", background: "#f8fafc", color: "#64748b", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePrice}
                style={{ flex: 1, padding: "12px", borderRadius: "10px", border: "none", background: "#2c4791", color: "white", fontSize: "14px", fontWeight: 600, cursor: "pointer" }}
              >
                Update Price
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview */}
      {previewImage && (
        <div className="image-preview-overlay" onClick={closePreview}>
          <button className="preview-close-btn" onClick={closePreview} aria-label="Close preview">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
          <div className="zoom-indicator">{Math.round(zoom * 100)}%</div>
          <div className="preview-image-container" onClick={(e) => e.stopPropagation()}>
            <img src={previewImage} alt="Full View" className="preview-image" style={{ transform: `scale(${zoom})` }}
              onWheel={(e) => { e.preventDefault(); if (e.deltaY < 0) setZoom(z => Math.min(z + 0.1, 3)); else setZoom(z => Math.max(z - 0.1, 1)); }}
              draggable={false}
            />
          </div>
          <div className="preview-controls">
            <button className="preview-control-btn" onClick={(e) => { e.stopPropagation(); setZoom(z => Math.max(z - 0.2, 1)); }} disabled={zoom <= 1}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M8 11h6M21 21l-4.35-4.35" /></svg>
              <span>Zoom Out</span>
            </button>
            <button className="preview-control-btn reset-btn" onClick={(e) => { e.stopPropagation(); setZoom(1); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 0118 0 9 9 0 01-18 0" /><path d="M12 8v8M8 12h8" /></svg>
              <span>Reset</span>
            </button>
            <button className="preview-control-btn" onClick={(e) => { e.stopPropagation(); setZoom(z => Math.min(z + 0.2, 3)); }} disabled={zoom >= 3}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M11 8v6M8 11h6M21 21l-4.35-4.35" /></svg>
              <span>Zoom In</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewProperties;